import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import AHDate from "_shared/dates/date";
import EmailAddress from "_shared/email-addresses/email-address";
import { EntityAgreementJSON } from "legal-entities/agreements/_schema/entity-agreement-schema";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import Company from "marketplace/companies/company";
import { CompanyJSON } from "marketplace/companies/company-schema";
import { Dispatch, SetStateAction } from "react";
import AuthEmailAPIService, { AuthResponse, ExpiredTokenError } from "users/_api/auth-email-api-service";
import { AccountType } from "users/account-types/account-type";
import { AuthContext } from "users/auth/context/auth-context";
import { AuthContextJSON } from "users/auth/context/auth-context-schema";
import UserCredentials from "users/auth/credentials/user-credentials";
import UserAuthToken from "users/auth/tokens/user-auth-token";
import { UserAuthTokenJSON } from "users/auth/tokens/user-auth-token-schema";
import UserInformation from "users/information/user-information";
import User from "users/user";
import UserSchema, { UserJSON } from "users/user-schema";

export interface SessionJSON {
  _user: UserJSON | null;
  _company: CompanyJSON | null;
  _context: AuthContextJSON | null;
  _openEntityAgreements: EntityAgreementJSON[];
  _authToken?: UserAuthTokenJSON;
}

class Session {
  private _user: User | null = null;
  private _company: Company | null = null;
  private _context: AuthContext | null = null;
  private _openEntityAgreements: EntityAgreement[] = [];
  private _authToken?: UserAuthToken;
  private _updateSession: Dispatch<SetStateAction<Session | undefined>> | undefined;

  public toJSON(): SessionJSON {
    return {
      _user: this._user ? this._user.toJSON() : this._user,
      _company: this._company ? this._company.toJSON() : this._company,
      _context: this._context ? this._context.toJSON() : this._context,
      _openEntityAgreements: this._openEntityAgreements?.map((agreement) => agreement.toJSON()) ?? [],
      _authToken: this._authToken ? (this._authToken.toJSON() ?? undefined) : undefined,
    };
  }
  public static loadFromStorage(updateSession: Dispatch<SetStateAction<Session | undefined>>): Session {
    const sessionData = localStorage.getItem("Session");
    const parsedSessionData = sessionData ? JSON.parse(sessionData) : null;

    if (!parsedSessionData?._authToken?.value) {
      const newSession = new Session();
      newSession._updateSession = updateSession;
      return newSession;
    }

    let session: Session;
    try {
      session = Session.fromJSON(parsedSessionData, updateSession);
    } catch (SessionLoadError) {
      console.error("Error loading session: ", SessionLoadError);
      localStorage.removeItem("Session");
      session = new Session();
      session._updateSession = updateSession;
    }
    return session;
  }

  public login = async (userCredentials: UserCredentials, remember: boolean) => {
    const authResponse: AuthResponse = await AuthEmailAPIService.login(userCredentials, remember);

    this.initializeFromAuthResponse(authResponse);
    document.dispatchEvent(new CustomEvent("loggedIn"));
    return authResponse;
  };

  public logout = async () => {
    localStorage.removeItem("Session");
    this._user = null;
    this._company = null;
    this._context = null;
    this._openEntityAgreements = [];
    this._authToken = undefined;
    globalThis.location.assign("/login");
  };

  public refreshFromUserInfo(userInfo: UserInformation) {
    this._user = User.create({
      name: userInfo.name,
      email: userInfo.email ?? EmailAddress.create(""),
      id: userInfo.id,
      isSuperUser: userInfo.isSuperUser,
      hasSigningAuthority: userInfo.hasSigningAuthority,
      individualId: userInfo.individualId,
      legalEntityId: userInfo.legalEntityId,
      companyName: userInfo.companyName,
    });
    this._context = AuthContext.create({
      viewingAsVendor: userInfo.viewingAsVendor,
    });
    this._openEntityAgreements = userInfo.openEntityAgreements.map((agreement) =>
      EntityAgreement.fromUserOpenEntityAgreement(agreement),
    );
    localStorage.setItem("Session", JSON.stringify(this));
  }

  public initializeFromAuthResponse(authResponse: AuthResponse) {
    this._user = authResponse.user;
    this._context = authResponse.context;
    this._authToken = authResponse.authToken;
    this._openEntityAgreements = authResponse.openEntityAgreements.map((agreement) =>
      EntityAgreement.fromUserOpenEntityAgreement(agreement),
    );
    localStorage.setItem("Session", JSON.stringify(this.toJSON()));
    AttorneyHubAPIService.setAuthHeader(this._authToken?.value);
    this._updateSession?.(this);
  }

  public async setAccountViewType(accountType: AccountType) {
    if (!this._updateSession) {
      console.warn("Cannot update session.");
    }
    if (!this.context) return;

    const newContext = AuthContext.create({
      viewingAsVendor: accountType === AccountType.Vendor,
    });

    await this.setContext(newContext);
    const updatedSession = Session.fromJSON(this.toJSON(), this._updateSession);
    this._updateSession?.(updatedSession);
    localStorage.setItem("Session", JSON.stringify(updatedSession.toJSON()));
  }

  public async setContext(newContext: AuthContext) {
    if (!this._context) return;
    this._context = newContext;
    localStorage.setItem("Session", JSON.stringify(this));
    if (this.isLoggedIn) {
      try {
        await AuthEmailAPIService.updateUserVendorView(this._context.viewingAsVendor ?? false, this.authToken);
      } catch (error) {
        if (error instanceof ExpiredTokenError) await this.logout();
        else throw error;
      }
    }
  }

  public get user(): User | null {
    return this._user;
  }

  public get context(): AuthContext | null {
    return this._context;
  }

  public get authToken(): UserAuthToken | undefined {
    return this._authToken;
  }

  public get company(): Company | null {
    return this._company;
  }

  public get openEntityAgreements(): EntityAgreement[] {
    return this._openEntityAgreements;
  }

  public get isLoggedIn(): boolean {
    return !!this._user && !!this._authToken && !this._authToken.isExpired;
  }

  public get accountType(): AccountType {
    if (!this._user || !this._context) return AccountType.None;
    return this._context?.viewingAsVendor ? AccountType.Vendor : AccountType.Client;
  }

  public updateAuthToken = (value?: UserAuthToken) => {
    this._authToken = value;
  };

  public static fromJSON(
    json: SessionJSON,
    updateSession: Dispatch<SetStateAction<Session | undefined>> | undefined,
  ): Session {
    const session = new Session();
    session._updateSession = updateSession;

    try {
      if (!json._authToken?.value) {
        throw new SessionLoadError("No auth token");
      }
      session._authToken = UserAuthToken.create({
        value: json._authToken.value,
        tokenExp: new AHDate(json._authToken.tokenExp),
      });
      AttorneyHubAPIService.setAuthHeader(session._authToken.value);
    } catch (error) {
      AttorneyHubAPIService.setAuthHeader(null);
      if (error instanceof Error && error.name === "InvalidTokenError") {
        throw new SessionLoadError("Invalid token");
      } else {
        console.error("Error parsing auth token:", error);
        throw new SessionLoadError("Error parsing auth token");
      }
    }

    session._user = new User(UserSchema.deserialize(json._user));
    session._context = json._context ? AuthContext.fromJSON(json._context) : null;
    session._openEntityAgreements = EntityAgreement.fromJSONArray(json._openEntityAgreements) ?? [];

    return session;
  }
}

export default Session;

export class SessionLoadError extends Error {}
